import { toast } from 'vue-sonner'
import VueCookies from 'vue-cookies'
import { useAuthStore } from '@/stores'

// Dev always uses same-origin `/api` (Vite proxy → VITE_BASE_API_URL).
// Prod: set VITE_API_SAME_ORIGIN=true only after the SPA host nginx proxies
// /api (+ /storage) to the API (see nginx.conf). Until then, absolute
// VITE_BASE_API_URL is used — but cross-origin uploads fail with a CORS error
// when the API/proxy returns 413/502 without Access-Control-Allow-Origin.
const useSameOriginApi =
  import.meta.env.DEV || import.meta.env.VITE_API_SAME_ORIGIN === 'true'
const baseUrl = useSameOriginApi ? '/api' : import.meta.env.VITE_BASE_API_URL

export class ApiError extends Error {
  constructor(message, status = null, data = null, { code, config, headers } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
    this.code = code
    this.config = config
    // `response` mirror so existing `error.response?.data?.message` reads keep working.
    if (status !== null) this.response = { status, data, headers }
  }
}

export function toastApiError(error) {
  const { data, status } = error
  const message = data?.message

  if (typeof message === 'string' && message.trim()) {
    toast.error(message)
    return
  }

  const errors = data?.errors
  if (Array.isArray(errors) && errors.length) {
    errors.forEach((item) => {
      toast.error(typeof item === 'string' ? item : String(item?.message ?? item))
    })
    return
  }

  if (errors && typeof errors === 'object') {
    const messages = Object.values(errors).flat().filter(Boolean)
    if (messages.length) {
      messages.forEach((item) => toast.error(String(item)))
      return
    }
  }

  if (status === 403) {
    toast.error("You don't have permission to perform this action.")
    return
  }

  if (status === null) {
    toast.error(
      error.code === 'ERR_NETWORK'
        ? 'Could not reach the server. Check your connection, or try again with a smaller upload if you were sending a file.'
        : (error.message || 'Network error'),
    )
  }
}

/** Query-string serializer (Laravel-style): `a[]=1&a[]=2`, `a[b]=c`, null/undefined skipped. */
function serializeParams(params) {
  const search = new URLSearchParams()
  const append = (key, value) => {
    if (value === undefined || value === null) return
    if (Array.isArray(value)) {
      const flat = isFlatArray(value)
      value.forEach((item, i) => append(flat ? `${key}[]` : `${key}[${i}]`, item))
    } else if (value instanceof Date) {
      search.append(key, value.toISOString())
    } else if (typeof value === 'object') {
      Object.entries(value).forEach(([k, v]) => append(`${key}[${k}]`, v))
    } else {
      search.append(key, String(value))
    }
  }
  Object.entries(params ?? {}).forEach(([key, value]) => append(key, value))
  return search.toString()
}

/** True when an array holds only primitives/Blobs — safe to send as `a[]` without indexes. */
const isFlatArray = (arr) =>
  arr.every((item) => item instanceof Blob || item instanceof Date || item === null || typeof item !== 'object')

/**
 * Plain object → FormData with Laravel-style keys (`a[b]`, `a[]`, null → '').
 * Arrays of objects/arrays are indexed (`answers[0][id]`): with bare `[]` PHP
 * starts a new element for every key, splitting each object apart.
 */
function toFormData(data) {
  const form = new FormData()
  const append = (key, value) => {
    if (value === undefined) return
    if (Array.isArray(value)) {
      const flat = isFlatArray(value)
      value.forEach((item, i) => append(flat ? `${key}[]` : `${key}[${i}]`, item))
    } else if (value instanceof Blob) {
      form.append(key, value)
    } else if (value instanceof Date) {
      form.append(key, value.toISOString())
    } else if (value && typeof value === 'object') {
      Object.entries(value).forEach(([k, v]) => append(`${key}[${k}]`, v))
    } else {
      form.append(key, value === null ? '' : String(value))
    }
  }
  Object.entries(data ?? {}).forEach(([key, value]) => append(key, value))
  return form
}

function joinUrl(base, url, params) {
  const absolute = /^https?:\/\//i.test(url)
  const path = absolute ? url : `${String(base ?? '').replace(/\/+$/, '')}/${String(url).replace(/^\/+/, '')}`
  const query = serializeParams(params)
  if (!query) return path
  return `${path}${path.includes('?') ? '&' : '?'}${query}`
}

async function readBody(response, responseType) {
  if (responseType === 'blob') return response.blob()
  if (responseType === 'arraybuffer') return response.arrayBuffer()
  const text = await response.text()
  if (!text) return ''
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

/**
 * Builds a fetch-based client with a promise-based call surface
 * (`get/post/put/patch/delete` resolving `{ data, status, headers }`, rejecting
 * with an ApiError that also exposes `error.response`).
 *
 * Request config: `params`, `headers`, `baseURL`, `responseType` ('json' | 'blob' |
 * 'arraybuffer'), `timeout` (ms), `signal`, `ignoreGlobalCatch`.
 */
export function createHttpClient({ baseURL, timeout = 0, onError } = {}) {
  async function request(method, url, data, config = {}) {
    const headers = { Accept: 'application/json', ...config.headers }
    const token = VueCookies.get('token') || null
    if (token) headers.Authorization = `Bearer ${token}`

    let body
    const contentTypeKey = Object.keys(headers).find((k) => k.toLowerCase() === 'content-type')
    const declaredType = contentTypeKey ? String(headers[contentTypeKey]) : ''
    if (data !== undefined && data !== null) {
      if (data instanceof FormData) {
        body = data
      } else if (declaredType.includes('multipart/form-data')) {
        body = toFormData(data)
      } else if (data instanceof Blob || data instanceof URLSearchParams || typeof data === 'string') {
        body = data
        if (!contentTypeKey && typeof data === 'string') headers['Content-Type'] = 'application/json'
      } else {
        body = JSON.stringify(data)
        headers['Content-Type'] = 'application/json'
      }
    }
    // The browser must add the multipart boundary itself; a hand-written header breaks uploads.
    if (body instanceof FormData || body instanceof URLSearchParams) {
      Object.keys(headers)
        .filter((k) => k.toLowerCase() === 'content-type')
        .forEach((k) => delete headers[k])
    }

    const timeoutMs = config.timeout ?? timeout
    const signals = [config.signal, timeoutMs > 0 ? AbortSignal.timeout(timeoutMs) : null].filter(Boolean)
    const signal = signals.length > 1 ? AbortSignal.any(signals) : signals[0]

    const errorConfig = { ignoreGlobalCatch: config.ignoreGlobalCatch }
    let error
    try {
      const response = await fetch(joinUrl(config.baseURL ?? baseURL, url, config.params), {
        method,
        headers,
        body,
        signal,
      })
      const payload = await readBody(response, config.responseType)
      const responseHeaders = Object.fromEntries(response.headers.entries())
      if (response.ok) return { data: payload, status: response.status, headers: responseHeaders }
      error = new ApiError(
        payload?.message || `Request failed with status ${response.status}`,
        response.status,
        payload,
        { config: errorConfig, headers: responseHeaders },
      )
    } catch (e) {
      if (e instanceof ApiError) throw e
      // A caller-initiated abort (e.g. TanStack Query cancelling a stale request) is not an error to report.
      if (e?.name === 'AbortError') throw e
      const timedOut = e?.name === 'TimeoutError'
      error = new ApiError(
        timedOut ? `timeout of ${timeoutMs}ms exceeded` : (e?.message || 'Network error'),
        null,
        null,
        { code: timedOut ? 'ECONNABORTED' : 'ERR_NETWORK', config: errorConfig },
      )
    }

    if (error.status === 401) {
      // Full teardown (stops the alerts poller too — see auth.store.js) and
      // a single clear "session expired" toast, regardless of
      // ignoreGlobalCatch: a forced logout must never happen silently.
      useAuthStore().SessionExpired()
    } else if (!config.ignoreGlobalCatch) {
      try {
        ;(onError ?? toastApiError)(error)
      } catch {
        // Never let toast formatting crash the app
      }
    }
    throw error
  }

  return {
    get: (url, config) => request('GET', url, undefined, config),
    delete: (url, config) => request('DELETE', url, config?.data, config),
    post: (url, data, config) => request('POST', url, data, config),
    put: (url, data, config) => request('PUT', url, data, config),
    patch: (url, data, config) => request('PATCH', url, data, config),
  }
}

export const http = createHttpClient({ baseURL: baseUrl })

/**
 * Endpoint-style call resolving the full `{ data, status, headers }` response
 * (use for downloads/previews where headers or the Blob matter).
 * `body` is sent as JSON or FormData.
 */
export function apiRequest(
  endpoint,
  { method = 'GET', params, body, headers, signal, ignoreGlobalCatch, responseType, baseURL, timeout } = {},
) {
  const config = { params, headers, signal, ignoreGlobalCatch, responseType, baseURL, timeout }
  const verb = method.toUpperCase()
  if (verb === 'GET') return http.get(endpoint, config)
  if (verb === 'DELETE') return http.delete(endpoint, { ...config, data: body })
  return http[verb.toLowerCase()](endpoint, body, config)
}

/**
 * Promise-returning helper for new code: resolves with the parsed body (the Laravel
 * `{ message, data }` envelope).
 */
export async function apiFetch(endpoint, options) {
  return (await apiRequest(endpoint, options)).data
}
